import React from 'react';
import { Drawer, Typography } from 'antd';

/**
 * What the words on these screens mean.
 *
 * An admin meets answer key, mint, telling, curated, verdict, quality audit,
 * PCS, ICS, lane, refusal and fingerprint before they have made a single
 * decision, and until now the module offered no tooltip, no help link and no
 * definition of any of them. Every entry below is written for the person who
 * has to act on it, not for the person who built it: what the thing IS, and
 * what it changes about what they should do.
 *
 * ONE VOCABULARY, and it is defined here first. In this product the marking
 * scheme is the ANSWER KEY — the scored view of every headline and period a
 * trainee is graded against. In a trading room a benchmark is an index you are
 * measured relative to, which is a different idea, and the word misled
 * everyone who met these screens including the people who built them. So the
 * visible labels say ANSWER KEY, and the session PERFORMANCE benchmark on the
 * blueprint page is always named in full. The database column is still called
 * `benchmark`, because renaming a stored contract to fix a label is how you
 * get two vocabularies instead of none.
 */

export type GlossaryEntry = { term: string; definition: string };

export const GLOSSARY: GlossaryEntry[] = [
  {
    term: 'Answer key (stored as "benchmark")',
    definition:
      'The scored, reviewed view of a scenario: for every period and every ' +
      "instrument, what the right read was. A trainee's answers are compared " +
      'against it, and that comparison is their score. It is not a market ' +
      'index — nothing here measures a trainee relative to one. It has no ' +
      'version number: an answer key IS its inputs, and the eight-character ' +
      'hash beside it is the name of exactly those inputs.',
  },
  {
    term: 'Graded / not graded',
    definition:
      'Said of one instrument in a session. GRADED means the answer key ' +
      "marks it: the trainee's conviction on it moves their score. NOT " +
      'GRADED means the key deliberately leaves it out, and says why in a ' +
      'sentence the trainee can read. Every tradable instrument is one or ' +
      'the other before a launch is allowed — silence is what used to let a ' +
      'candidate work all session on a row that could never count.',
  },
  {
    term: 'Started session',
    definition:
      'What a trainee actually sees, which is not what the template lists. ' +
      'PM adds instruments at the moment a session starts — the currency ' +
      'pairs needed to exchange money, above all — so the answer key is ' +
      'derived against the STARTED shape, never the template alone.',
  },
  {
    term: 'Added by PM',
    definition:
      'An instrument the template never named and PM injected at start, so ' +
      'trainees can exchange currency. It is tradable and it is real; the ' +
      'answer key does not usually mark it, and the row says so rather than ' +
      'leaving the trainee to guess.',
  },
  {
    term: 'Retired answer key',
    definition:
      'A key taken out of service for good, because its family was ' +
      'regenerated and a new key replaced it. Nothing is ever deleted: every ' +
      'session it already graded keeps producing the result it produced, and ' +
      'the screens that show those scores say which retired key produced ' +
      'them. A retired key grades nobody new.',
  },
  {
    term: 'The matrix',
    definition:
      'The one table of factors the scorer reads to turn a headline into an ' +
      'expected move, per instrument. It extends itself: a factor it has ' +
      'never seen is added and tagged with where it came from, and a column ' +
      'it cannot resolve is reported by name rather than quietly dropped.',
  },
  {
    term: 'Mint',
    definition:
      'Turning an authored scenario draft into something trainees can ' +
      'actually play: a blueprint version with its news, its turn schedule ' +
      'and its instruments. A dry run mints nothing; the apply writes.',
  },
  {
    term: 'Telling',
    definition:
      'The exact set of headlines, timestamps and briefings one mint ships. ' +
      "The same draft can be told twice — once from the curators' reviewed " +
      'sheet, once from the model — and the two tellings differ.',
  },
  {
    term: 'Curated',
    definition:
      'A draft whose headlines a reviewer has ruled on, one by one: kept, ' +
      'edited, dropped, or added as filler. A curated draft mints with no ' +
      'model and no corpus, which is why it works in any environment.',
  },
  {
    term: 'Verdict',
    definition:
      "The quality audit's grading of one answer key: pass, warn, " +
      'insufficient, or fail. It is stored with the checks that produced it ' +
      'and a fingerprint of the content it graded, so a verdict about ' +
      'different content is treated as no verdict at all.',
  },
  {
    term: 'Quality audit',
    definition:
      'The set of checks that ask whether an answer key can tell skill from ' +
      'clicking. It never edits anything and never blocks a warning; what it ' +
      'blocks is activating an answer key it has failed, unless an admin ' +
      'overrides it with a written reason that is stored. A key that fails ' +
      'it stays a draft, and launches of that family are refused by name.',
  },
  {
    term: 'Consistency by period (PCS)',
    definition:
      'Within one period, how consistently the answer key says an instrument ' +
      'should be traded one way. Near +1 or -1 means the period has a clear ' +
      'right answer; near 0 means it does not, and grading a trainee on it ' +
      'is close to grading a coin toss.',
  },
  {
    term: 'Consistency by headline (ICS)',
    definition:
      'The same question one headline at a time: how strongly this single ' +
      'piece of news points at this instrument. It is what lets a trainer ' +
      'say which headline a trainee should have acted on.',
  },
  {
    term: 'Lane',
    definition:
      'Which machine does the work. The slim lane is the ordinary web ' +
      'workers and runs everywhere. The heavy lane needs the corpus service ' +
      'and a model; work that needs it waits (BLOCKED) until that service is ' +
      'running, rather than failing.',
  },
  {
    term: 'Refusal',
    definition:
      'A named check stopped a job BEFORE it wrote anything, and it states ' +
      'what would fix it. A refusal is not a crash and not a failure: ' +
      'nothing was written, and re-running the same input gets the same ' +
      'answer, so the remedy is the next action, not a retry.',
  },
  {
    term: 'Fingerprint',
    definition:
      'A hash of what a mint wrote, kept so the next mint can tell whether ' +
      "anybody has edited that version in pm's studio since. If it no longer " +
      "matches, re-minting overwrites somebody's work, and the screen asks " +
      'you to say so before it does.',
  },
];

const Glossary: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => (
  <Drawer
    open={open}
    onClose={onClose}
    width={620}
    title="What the words on these screens mean"
    destroyOnHidden
  >
    <Typography.Paragraph type="secondary">
      Written for the person deciding, not the person who built it. Where a
      label here differs from the name in the API, both are given.
    </Typography.Paragraph>
    {GLOSSARY.map(entry => (
      <div key={entry.term} style={{ marginBottom: 16 }}>
        <Typography.Text strong>{entry.term}</Typography.Text>
        <Typography.Paragraph style={{ marginBottom: 0 }}>
          {entry.definition}
        </Typography.Paragraph>
      </div>
    ))}
  </Drawer>
);

export default Glossary;
